import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../../hooks/useApiHelper";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { usePartVatResolution } from "../../data/useVatMarginScheme";
import { IPart } from "../../models/Part";
import { DefaultResponse } from "../../models/system";

const OPTIONS = [
  { value: "", label: "Inherit from vehicle" },
  { value: "1", label: "Force standard VAT" },
  { value: "2", label: "Force margin scheme" },
];

interface PartVatTreatmentOverrideProps {
  part: IPart;
}

/** Admin control for >£500 / exception components under Global Margin Scheme. */
export default function PartVatTreatmentOverride({ part }: PartVatTreatmentOverrideProps) {
  const { put } = useApiHelper();
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Part VAT treatment");
  const { resolution } = usePartVatResolution(part.id);

  const current =
    part.vatSalesTreatmentOverride == null || part.vatSalesTreatmentOverride === 0
      ? ""
      : String(part.vatSalesTreatmentOverride);

  const mutation = useMutation(
    (treatment: number | null) =>
      put(`/vat/part/${part.id}/treatment-override`, {
        treatment,
      }) as Promise<DefaultResponse>,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["part", part.id]);
        queryClient.invalidateQueries(["parts"]);
        queryClient.invalidateQueries(["part-vat-resolution", part.id]);
        queryClient.invalidateQueries(["omniSearchVat"]);
        saveHandlers.onSuccess();
      },
      onError: () => saveHandlers.onError(),
    }
  );

  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">
          Sales VAT treatment override
        </label>
        {resolution && (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              resolution.isMarginScheme
                ? "bg-amber-100 text-amber-800"
                : "bg-green-100 text-green-800"
            }`}
          >
            {resolution.isMarginScheme
              ? "Margin scheme"
              : `Standard VAT ${resolution.taxRate}%`}
            {current === "" && " (from vehicle)"}
          </span>
        )}
      </div>
      <select
        className="w-full text-sm border-gray-300 rounded-md focus:border-primary-500 focus:ring-primary-500"
        value={current}
        disabled={mutation.isLoading}
        onChange={(e) => {
          const raw = e.target.value;
          mutation.mutate(raw === "" ? null : Number(raw));
        }}
      >
        {OPTIONS.map((o) => (
          <option key={o.value || "inherit"} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <p className="text-xs text-gray-500 mt-1">
        Use “Force standard VAT” for high-value components excluded from global accounting (£500+).
      </p>
    </div>
  );
}
